import { useState, useEffect } from "react";
import { store } from "store/createStore";
import _ from "lodash";

/**
 * Custom hook to subscribe to a Redux store and select a piece of state.
 *
 * @param {Function} selector - A function that selects a piece of state from the Redux store.
 * @param {Function} [equalityFn=_.isEqual] - A function to compare the current state with the new state.
 * @returns {*} The selected piece of state from the Redux store.
 *
 * @note Replace this with the useSelector hook from redux once it becomes available.
 */
export const useReduxState = (selector, equalityFn = _.isEqual) => {
  const [state, setState] = useState(selector(store.getState()));

  useEffect(() => {
    const checkForUpdates = () => {
      const newState = selector(store.getState());
      setState(prevState => {
        if (equalityFn(prevState, newState)) {
          return prevState; // No change, return previous state
        }
        return newState; // State has changed, return new state
      });
    };

    const unsubscribe = store.subscribe(checkForUpdates);

    return () => {
      unsubscribe();
    };
  }, [selector, equalityFn]);

  return state;
};
